<script lang="ts">
  import { untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import CreateWorkflowActionBox from "../../../create-workflow-action-box"
  import CreateWorkflowStepFrame from "../../../create-workflow-step-frame"
  import { statusLabels, toIsoString } from "../../../../../../../../../_shared/_logic/form-state"
  import { internalOrderTypeLabel } from "../../../../../../../../../_shared/_logic/order-type-labels"
  import { selectedOrderTypeItems } from "../../../../../../../_shared/_logic/order-type-selection"
  import CreateAppointmentAction from "./_modules/create-appointment-action"
  import type {
    AppointmentAnnouncementOutput,
    AppointmentCreateInit,
    AppointmentGeneralOutput,
    AppointmentPlanningOutput,
  } from "../../../../_logic/create-appointment-contract"

  type Props = {
    init: AppointmentCreateInit
    general: AppointmentGeneralOutput
    planning: AppointmentPlanningOutput
    output?: AppointmentAnnouncementOutput
    readOnly?: boolean
    onBack: () => void
    onWorkOrderCreated?: (output: AppointmentGeneralOutput) => Promise<void> | void
    onCreated: (event: ScheduleEvent) => Promise<void> | void
    onClose: () => void
    onError?: (message: string | null) => void
  }

  const {
    init,
    general,
    planning,
    output = undefined,
    readOnly = false,
    onBack,
    onWorkOrderCreated = async () => {},
    onCreated,
    onClose,
    onError = () => {},
  }: Props = $props()

  const initialAnnouncement = untrack(() => ({
    enabled: general.workOrderSource === "none" ? false : (output?.enabled ?? true),
  }))

  let enabled = $state(initialAnnouncement.enabled)
  const outputValue = $derived<AppointmentAnnouncementOutput>({
    enabled,
  })
  const validationMessage = $derived(null)
  const canCreate = $derived(!validationMessage)

  const formatDateTime = (value: unknown) => {
    const iso = toIsoString(value as any)

    if (!iso) return "Nicht ausgewählt"

    const parsed = new Date(iso)

    if (Number.isNaN(parsed.getTime())) return "Nicht ausgewählt"

    return parsed.toLocaleString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatAppointmentTime = () =>
    `${formatDateTime(planning.startAt)} bis ${formatDateTime(planning.endAt)}`
  const selectedEmployeeLabel = $derived(
    init.employees.find((employee) => employee.id === planning.employeeId)?.label ??
      "Nicht ausgewählt",
  )
  const selectedWorkOrderLabel = $derived.by(() => {
    if (general.workOrderSource === "none") return "Kein Auftrag"

    if (general.workOrderSource === "new") {
      const orderNumber = `${general.newWorkOrderDraft?.order_number ?? ""}`.trim()
      const draft = general.newWorkOrderDraft
      const orderType = draft
        ? (internalOrderTypeLabel({
            order_types: selectedOrderTypeItems(draft, init.orderTypes ?? []),
          }) ?? "")
        : ""

      return [orderType || "Neuer Auftrag", orderNumber ? `#${orderNumber}` : ""]
        .filter(Boolean)
        .join(" ")
    }

    return (
      general.selectedWorkOrder?.label ??
      internalOrderTypeLabel(
        init.workOrders.find(
          (entry) => `${entry.id ?? ""}`.trim() === `${general.workOrderId ?? ""}`.trim(),
        ),
      ) ??
      "Auftrag ausgewählt"
    )
  })
  const flexTypeLabel = $derived(planning.flexType === "SOFT" ? "Flextermin" : "Festtermin")
  const statusLabel = $derived(statusLabels[planning.status] ?? planning.status)
</script>

<form onsubmit={(event) => event.preventDefault()}>
  <CreateWorkflowStepFrame
    title="Ankündigung"
    description="Prüfen Sie kurz, was beim Anlegen passiert."
    helpModalTextId="appointmentCreateAnnouncement"
  >
    <CreateWorkflowActionBox label="Zusammenfassung">
      <p>
        {general.workOrderSource === "none"
          ? "KehrBlatt legt einen freien Termin ohne Auftragsverknüpfung an."
          : "KehrBlatt legt den Termin an und erstellt wie bisher den Ankündigungsplan, sofern der Auftrag dafür geeignet ist."}
      </p>
      <dl class="summary-grid">
        <div>
          <dt>Auftrag</dt>
          <dd>{selectedWorkOrderLabel}</dd>
        </div>
        <div>
          <dt>Mitarbeiter</dt>
          <dd>{selectedEmployeeLabel}</dd>
        </div>
        <div>
          <dt>Terminzeit</dt>
          <dd>{formatAppointmentTime()}</dd>
        </div>
        <div>
          <dt>Terminart</dt>
          <dd>{flexTypeLabel}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{statusLabel}</dd>
        </div>
        <div>
          <dt>Ankündigung</dt>
          <dd>{enabled ? "Ankündigungsplan wird angelegt" : "Ohne Ankündigungsplan"}</dd>
        </div>
      </dl>
      {#if !enabled}
        <p class="muted">
          Der Termin wird ohne Ankündigungsplan angelegt. Das kann später in der Terminansicht
          nachgeholt werden.
        </p>
      {/if}
    </CreateWorkflowActionBox>
  </CreateWorkflowStepFrame>
  {#if !readOnly}
    <div class="step-actions">
      <CTA type="secondaryButton" content="Zurück" onClick={onBack} />
      <CreateAppointmentAction
        {init}
        {general}
        {planning}
        announcement={outputValue}
        disabled={!canCreate}
        disabledReason={validationMessage}
        {onWorkOrderCreated}
        {onCreated}
        {onClose}
        {onError}
      />
    </div>
  {/if}
</form>

<style>
  .muted {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    line-height: 1.35;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 0.85rem 1rem;
    margin: 0;
  }

  .summary-grid div {
    min-width: 0;
  }

  .summary-grid dt {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .summary-grid dd {
    margin: 0.1rem 0 0;
    color: var(--text-100);
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }
</style>
